import React from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  CalendarIcon, Search, FileText, Shield, Download, 
  Newspaper, Building2, Scale, Users, ChevronRight, Mail,
  CheckCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import api, { getApiBaseUrl } from '@/lib/api';

// Images
const CHALE_IMAGE = "https://customer-assets.emergentagent.com/job_321423f8-7787-4cc3-8d65-76e41f468946/artifacts/0c3n1pqb_Chal%C3%A9.jpg";
const LOGO_DOEM_URL = "/logo_doem.png";

const CATEGORIAS = [
  { id: 'Executivo', label: 'Diário do Executivo', icon: Building2, color: 'bg-[#B91C1C]' },
  { id: 'Legislativo', label: 'Diário do Legislativo', icon: Scale, color: 'bg-[#1D4ED8]' },
  { id: 'Terceiro Setor', label: 'Diário de Terceiros', icon: Users, color: 'bg-[#059669]' },
];

export default function HomePage() {
  const [selectedDate, setSelectedDate] = React.useState(new Date());
  const [edicoes, setEdicoes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState(null);
  const [newsletterEmail, setNewsletterEmail] = React.useState('');
  const [newsletterName, setNewsletterName] = React.useState('');
  const [whatsapp, setWhatsapp] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    fetchEdicoes();
    fetchStats();
  }, []);

  const fetchEdicoes = async () => {
    try {
      const response = await api.get('/edicoes-publicas', { params: { limit: 10 } });
      setEdicoes(response.data);
    } catch (error) {
      console.error('Error fetching edicoes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/estatisticas');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterName) {
      toast.error('Preencha nome e e-mail');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/newsletter/inscrever', {
        email: newsletterEmail,
        nome: newsletterName,
        categorias_interesse: [],
        whatsapp: whatsapp || null
      });
      toast.success('Inscrição realizada com sucesso!');
      setNewsletterEmail('');
      setNewsletterName('');
      setWhatsapp('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao realizar inscrição');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getCategoryBadgeClass = (categoria) => {
    switch (categoria) {
      case 'Executivo': return 'bg-[#B91C1C] text-white';
      case 'Legislativo': return 'bg-[#1D4ED8] text-white';
      case 'Terceiro Setor': return 'bg-[#059669] text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <Layout>
      {/* Hero Section with Chalé background */}
      <section 
        className="relative text-white py-20 overflow-hidden" 
        data-testid="hero-section"
        style={{
          backgroundImage: `linear-gradient(rgba(31, 41, 55, 0.85), rgba(31, 41, 55, 0.9)), url(${CHALE_IMAGE})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Logo with glow effect, text and Date Picker */}
            <div>
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8">
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-white/40 rounded-full blur-2xl scale-110 animate-pulse"></div>
                  <img 
                    src={LOGO_DOEM_URL}
                    alt="DEMOAC - Diário Eletrônico Municipal de Acaiaca"
                    className="h-40 md:h-48 w-auto relative z-10 drop-shadow-2xl"
                  />
                </div>
                {/* Texto DEMOAC com brilho */}
                <div className="relative flex flex-col justify-center text-center sm:text-left">
                  <div className="absolute inset-0 bg-[#FFD700]/20 blur-2xl scale-125 rounded-lg"></div>
                  <span className="relative z-10 text-[#FFD700] font-heading font-bold text-4xl md:text-5xl tracking-wide drop-shadow-[0_0_20px_rgba(255,215,0,0.8)]" style={{textShadow: '0 0 30px rgba(255,215,0,0.9), 0 0 60px rgba(255,215,0,0.5)'}}>
                    DEMOAC
                  </span>
                  <span className="relative z-10 text-[#FFD700] font-heading text-xs md:text-sm tracking-widest mt-1 drop-shadow-[0_0_15px_rgba(255,215,0,0.7)]" style={{textShadow: '0 0 20px rgba(255,215,0,0.7), 0 0 40px rgba(255,215,0,0.4)'}}>
                    DIÁRIO ELETRÔNICO MUNICIPAL
                  </span>
                  <span className="relative z-10 text-[#FFD700] font-heading text-xs md:text-sm tracking-widest drop-shadow-[0_0_15px_rgba(255,215,0,0.7)]" style={{textShadow: '0 0 20px rgba(255,215,0,0.7), 0 0 40px rgba(255,215,0,0.4)'}}>
                    DE ACAIACA
                  </span>
                </div>
              </div>
              
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4 text-center lg:text-left">
                Acesse a<br />
                <span className="text-[#FCD34D]">edição do dia</span>
              </h2>
              
              {/* Date Picker */}
              <div className="flex justify-center lg:justify-start">
                <div className="bg-white rounded-lg p-4 inline-block shadow-lg">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="text-gray-800 hover:bg-gray-100 text-left justify-start gap-3"
                      data-testid="date-picker-btn"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarIcon className="text-[#B91C1C]" size={24} />
                        <div className="text-left">
                          <div className="text-3xl font-bold text-[#B91C1C] leading-tight">
                            {format(selectedDate, 'dd', { locale: ptBR })}
                          </div>
                          <div className="text-sm text-gray-600 capitalize">
                            {format(selectedDate, 'MMMM yyyy', { locale: ptBR })}
                          </div>
                          <div className="text-xs text-gray-500 capitalize">
                            {format(selectedDate, 'EEEE', { locale: ptBR })}
                          </div>
                        </div>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => date && setSelectedDate(date)}
                      locale={ptBR}
                      className="rounded-md border"
                    />
                  </PopoverContent>
                </Popover>
                </div>
              </div>
            </div>

            {/* Right - Category Cards */}
            <div className="space-y-3">
              {CATEGORIAS.map((cat) => {
                const Icon = cat.icon;
                return (
                  <Link 
                    key={cat.id}
                    to={`/buscar?categoria=${encodeURIComponent(cat.id)}`}
                    className="block"
                  >
                    <Card className="bg-white/95 backdrop-blur hover:shadow-lg transition-all hover:scale-[1.02] cursor-pointer" data-testid={`category-${cat.id.toLowerCase().replace(' ', '-')}`}>
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`${cat.color} p-3 rounded-lg`}>
                            <Icon className="text-white" size={24} />
                          </div>
                          <span className="font-semibold text-gray-800">{cat.label}</span>
                        </div>
                        <ChevronRight className="text-gray-400" size={20} />
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="bg-[#B91C1C] dark:bg-[#7F1D1D] py-8" data-testid="search-section">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/buscar" className="block">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4 flex items-center gap-4">
                <Search className="text-gray-400" size={24} />
                <span className="text-gray-500 flex-1">Clique aqui para efetuar a pesquisa avançada</span>
                <ChevronRight className="text-gray-400" size={20} />
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-[#F9FAFB] dark:bg-[#1F2937]" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-bold text-center mb-4 dark:text-white">
            Um <span className="text-[#B91C1C]">Diário</span> feito para você!
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {/* Feature 1 */}
            <Card className="text-center hover:shadow-lg transition-shadow dark:bg-[#374151]">
              <CardContent className="pt-8 pb-6">
                <div className="w-48 h-32 mx-auto mb-6 rounded-lg overflow-hidden bg-gradient-to-br from-[#B91C1C] to-[#991B1B] flex items-center justify-center">
                  <FileText className="text-white" size={64} />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2 dark:text-white">Digitalização do Diário Oficial</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Confira informações e novidades do projeto que busca disponibilizar todo o acervo do jornal na palma da mão
                </p>
                <Link to="/buscar">
                  <Button className="bg-[#B91C1C] hover:bg-[#991B1B]">Ver mais</Button>
                </Link>
              </CardContent>
            </Card>

            {/* Feature 2 */}
            <Card className="text-center hover:shadow-lg transition-shadow dark:bg-[#374151]">
              <CardContent className="pt-8 pb-6">
                <div className="w-48 h-32 mx-auto mb-6 rounded-lg overflow-hidden bg-gradient-to-br from-[#059669] to-[#047857] flex items-center justify-center">
                  <Shield className="text-white" size={64} />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2 dark:text-white">Autenticidade da publicação</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Consulte aqui a veracidade das publicações de seu interesse com assinatura digital
                </p>
                <Link to="/verificar">
                  <Button className="bg-[#B91C1C] hover:bg-[#991B1B]">Ver mais</Button>
                </Link>
              </CardContent>
            </Card>

            {/* Feature 3 */}
            <Card className="text-center hover:shadow-lg transition-shadow dark:bg-[#374151]">
              <CardContent className="pt-8 pb-6">
                <div className="w-48 h-32 mx-auto mb-6 rounded-lg overflow-hidden bg-gradient-to-br from-[#1D4ED8] to-[#1E40AF] flex items-center justify-center">
                  <Newspaper className="text-white" size={64} />
                </div>
                <h3 className="font-heading font-bold text-xl mb-2 dark:text-white">Acervo de Publicações</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm mb-4">
                  Acesse aqui o acervo completo de todas as publicações oficiais do município
                </p>
                <Link to="/buscar">
                  <Button className="bg-[#B91C1C] hover:bg-[#991B1B]">Ver mais</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Recent Editions */}
      <section className="py-16 bg-white dark:bg-[#111827]" data-testid="recent-editions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading text-2xl font-bold dark:text-white">Edições Recentes</h2>
            <Link to="/buscar">
              <Button variant="outline" className="border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white">
                Ver todas
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : edicoes.length === 0 ? (
            <Card className="dark:bg-[#374151]">
              <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
                <Newspaper className="mx-auto mb-4 text-gray-300 dark:text-gray-600" size={48} />
                <p>Nenhuma edição publicada ainda</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {edicoes.map((ed) => (
                <Card key={ed.edicao_id} className="hover:shadow-md transition-shadow pub-card dark:bg-[#374151]" data-testid={`edition-${ed.edicao_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-bold text-lg dark:text-white">
                            Edição nº {ed.numero_edicao}/{ed.ano}
                          </h3>
                          <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Publicado
                          </Badge>
                          {ed.assinatura_digital?.assinado && (
                            <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              <CheckCircle size={12} className="mr-1" />
                              Assinado
                            </Badge>
                          )}
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                          {formatDate(ed.data_publicacao)} • {ed.total_publicacoes} publicação(ões)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {ed.publicacoes?.slice(0, 3).map((pub, i) => (
                            <Badge key={i} className={getCategoryBadgeClass(pub.categoria)}>
                              {pub.categoria}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Link to={`/edicao/${ed.edicao_id}`}>
                          <Button variant="outline" className="border-gray-300 dark:border-gray-600 dark:text-gray-300">
                            <FileText size={16} className="mr-2" />
                            Ver
                          </Button>
                        </Link>
                        <Button 
                          className="bg-[#B91C1C] hover:bg-[#991B1B]"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = `${getApiBaseUrl()}/api/edicoes/${ed.edicao_id}/pdf`;
                            link.download = `DEMOAC_Edicao_${ed.numero_edicao}_${ed.ano}.pdf`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          <Download size={16} className="mr-2" />
                          PDF
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 bg-[#B91C1C] dark:bg-[#7F1D1D] text-white" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Mail className="mx-auto mb-4" size={48} />
            <h2 className="font-heading text-3xl font-bold mb-2">Receba as publicações por e-mail</h2>
            <p className="text-white/80">Inscreva-se para receber notificações das novas edições do Diário Oficial</p>
          </div>

          <form onSubmit={handleNewsletterSubmit} className="space-y-4 max-w-xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                type="text"
                placeholder="Seu nome"
                value={newsletterName}
                onChange={(e) => setNewsletterName(e.target.value)}
                className="bg-white text-gray-800"
                data-testid="newsletter-name"
              />
              <Input
                type="email"
                placeholder="Seu e-mail"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-white text-gray-800"
                data-testid="newsletter-email"
              />
            </div>
            <Input
              type="tel"
              placeholder="WhatsApp (opcional) - Ex: 31999999999"
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              className="bg-white text-gray-800"
              data-testid="newsletter-whatsapp"
            />
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-[#1F2937] hover:bg-[#374151]"
              data-testid="newsletter-submit"
            >
              {submitting ? 'Enviando...' : 'Inscrever-se'}
            </Button>
          </form>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="py-12 bg-white dark:bg-[#111827]" data-testid="stats-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-[#B91C1C]">{stats.total_edicoes_publicadas}</div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">Edições Publicadas</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#B91C1C]">{stats.total_publicacoes}</div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">Publicações</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#B91C1C]">{stats.por_categoria?.Executivo || 0}</div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">Atos Executivo</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-[#B91C1C]">{stats.por_categoria?.Legislativo || 0}</div>
                <div className="text-gray-600 dark:text-gray-400 text-sm">Atos Legislativo</div>
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}
